import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  deleteTemplate, forkCatalogEntry, getCatalogEntry, getTemplate, listCatalog,
  listTemplates, sendCatalogEntry, sendTemplate,
  type AuthenticatedSession, type CatalogDetail, type CatalogSummary,
  type CreatedRecipient, type Template, type TemplateDetail,
} from '../api'
import { KeepFor } from '../components/KeepFor'
import { OrganizationPicker, SentBy } from '../components/SentBy'
import { kindLabel } from '../fields'
import { dateTime } from '../format'
import { keptFor, retentionDaysOf } from '../keepFor'
import { alreadySet, templateSize } from '../templateSize'

type Tab = 'library' | 'mine'

const INDUSTRY_LABELS: Record<string, string> = {
  construction: 'Construction',
  'real-estate': 'Real estate',
  startups: 'Startups',
  consultants: 'Consultants',
}

function industryLabel(slug: string): string {
  return INDUSTRY_LABELS[slug] ?? slug
}

function fieldSummary(entry: CatalogSummary): string {
  const parts = [`${entry.page_count} page${entry.page_count === 1 ? '' : 's'}`]
  parts.push(`${entry.role_count} signer${entry.role_count === 1 ? '' : 's'}`)
  parts.push(`${entry.field_count} field${entry.field_count === 1 ? '' : 's'}`)
  return parts.join(' · ')
}

/**
 * Templates is the "send that again" surface, plus a library of prebuilt layouts
 * by industry.
 */
export function Templates({ session }: { session: AuthenticatedSession }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') === 'mine' ? 'mine' : 'library') as Tab
  const industry = searchParams.get('industry') ?? ''

  const [templates, setTemplates] = useState<Template[] | null>(null)
  const [catalog, setCatalog] = useState<CatalogSummary[] | null>(null)
  const [industries, setIndustries] = useState<string[]>([])
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [using, setUsing] = useState<TemplateDetail | null>(null)
  const [usingCatalog, setUsingCatalog] = useState<CatalogDetail | null>(null)
  const [forking, setForking] = useState('')

  const orgID = session.memberships[0]?.organization_id ?? ''

  const refreshMine = useCallback(() => {
    listTemplates()
      .then((r) => setTemplates(r.templates))
      .catch((e) => setError(e.message))
  }, [])

  const refreshCatalog = useCallback(() => {
    listCatalog(industry || undefined)
      .then((r) => {
        setCatalog(r.entries)
        setIndustries(r.industries)
      })
      .catch((e) => setError(e.message))
  }, [industry])

  useEffect(refreshMine, [refreshMine])
  useEffect(refreshCatalog, [refreshCatalog])

  function setTab(next: Tab) {
    const params = new URLSearchParams(searchParams)
    if (next === 'mine') {
      params.set('tab', 'mine')
    } else {
      params.delete('tab')
    }
    setSearchParams(params, { replace: true })
  }

  function setIndustry(next: string) {
    const params = new URLSearchParams(searchParams)
    if (next) {
      params.set('industry', next)
    } else {
      params.delete('industry')
    }
    params.delete('tab')
    setSearchParams(params, { replace: true })
  }

  async function openTemplate(id: string) {
    setError('')
    try {
      setUsing(await getTemplate(id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that template.')
    }
  }

  async function openCatalog(slug: string) {
    setError('')
    try {
      setUsingCatalog(await getCatalogEntry(slug))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that template.')
    }
  }

  async function fork(slug: string) {
    if (!orgID) return
    setForking(slug)
    setError('')
    setNotice('')
    try {
      await forkCatalogEntry(slug, { organization_id: orgID })
      setNotice('Added to My templates.')
      refreshMine()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not copy that template.')
    } finally {
      setForking('')
    }
  }

  async function remove(t: Template) {
    if (!confirm(`Delete the template "${t.name}"? Documents already sent from it are not affected.`)) {
      return
    }
    setError('')
    try {
      await deleteTemplate(t.id)
      refreshMine()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete that template.')
    }
  }

  if (using) {
    return <SendTemplate session={session} detail={using} onCancel={() => setUsing(null)} />
  }

  if (usingCatalog) {
    return (
      <SendCatalog
        session={session}
        detail={usingCatalog}
        onCancel={() => setUsingCatalog(null)}
      />
    )
  }

  return (
    <main className="page page-narrow">
      <div className="row-between page-head">
        <div>
          <h1>Templates</h1>
          <p>Start from a prebuilt layout or send one you saved before.</p>
        </div>
      </div>

      <div className="filter-tabs" role="tablist" aria-label="Template views" style={{ marginBottom: '1.25rem' }}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'library'}
          className={`filter-tab${tab === 'library' ? ' is-active' : ''}`}
          onClick={() => setTab('library')}
        >
          Library
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'mine'}
          className={`filter-tab${tab === 'mine' ? ' is-active' : ''}`}
          onClick={() => setTab('mine')}
        >
          My templates
        </button>
      </div>

      {error && <div className="notice notice-bad">{error}</div>}
      {notice && <div className="notice notice-good">{notice}</div>}

      {tab === 'library' ? (
        <LibraryView
          catalog={catalog}
          industries={industries}
          industry={industry}
          onIndustry={setIndustry}
          onUse={(slug) => void openCatalog(slug)}
          onFork={(slug) => void fork(slug)}
          forking={forking}
        />
      ) : (
        <MyTemplatesView
          templates={templates}
          onUse={(id) => void openTemplate(id)}
          onRemove={(t) => void remove(t)}
        />
      )}
    </main>
  )
}

function LibraryView({
  catalog,
  industries,
  industry,
  onIndustry,
  onUse,
  onFork,
  forking,
}: {
  catalog: CatalogSummary[] | null
  industries: string[]
  industry: string
  onIndustry: (slug: string) => void
  onUse: (slug: string) => void
  onFork: (slug: string) => void
  forking: string
}) {
  const pills = useMemo(() => ['', ...industries], [industries])

  return (
    <>
      <div className="filter-tabs" role="tablist" aria-label="Filter by industry" style={{ marginBottom: '1rem' }}>
        {pills.map((slug) => (
          <button
            key={slug || 'all'}
            type="button"
            role="tab"
            aria-selected={industry === slug}
            className={`filter-tab${industry === slug ? ' is-active' : ''}`}
            onClick={() => onIndustry(slug)}
          >
            {slug ? industryLabel(slug) : 'All'}
          </button>
        ))}
      </div>

      {catalog === null ? (
        <p className="muted">Loading…</p>
      ) : catalog.length === 0 ? (
        <div className="card empty">
          <h2>No templates here</h2>
          <p className="muted">Try another industry filter.</p>
        </div>
      ) : (
        <div className="stack">
          {catalog.map((entry) => (
            <div className="card" key={entry.slug}>
              <div className="row-between" style={{ alignItems: 'flex-start', gap: '1rem' }}>
                <div>
                  <div className="page-label">{industryLabel(entry.industry)}</div>
                  <strong>{entry.name}</strong>
                  <p className="muted small" style={{ margin: '.35rem 0 .5rem' }}>{entry.description}</p>
                  <div className="faint small">{fieldSummary(entry)}</div>
                  <p className="faint small" style={{ marginTop: '.5rem', marginBottom: 0 }}>
                    {entry.legal_note}
                  </p>
                </div>
                <div className="row" style={{ flexShrink: 0 }}>
                  <button className="btn" onClick={() => onUse(entry.slug)}>Use</button>
                  <button
                    className="btn btn-ghost"
                    disabled={forking === entry.slug}
                    onClick={() => onFork(entry.slug)}
                  >
                    {forking === entry.slug ? 'Adding…' : 'Add to my templates'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export function MyTemplatesView({
  templates,
  onUse,
  onRemove,
}: {
  templates: Template[] | null
  onUse: (id: string) => void
  onRemove: (t: Template) => void
}) {
  if (templates === null) {
    return <p className="muted">Loading…</p>
  }
  if (templates.length === 0) {
    return (
      <div className="card empty">
        <h2>No templates yet</h2>
        <p className="muted">
          Send a document and choose <strong>Save as template</strong>, or add one from the Library tab.
        </p>
      </div>
    )
  }
  return (
    <div className="stack">
      {templates.map((t) => (
        <div className="card" key={t.id}>
          <div className="row-between">
            <div>
              <strong>{t.name}</strong>
              <div className="faint small">
                {templateSize(t)} · saved {dateTime(t.created_at)}
              </div>
            </div>
            <div className="row">
              <button className="btn" onClick={() => onUse(t.id)}>Use</button>
              <button className="btn btn-ghost" onClick={() => onRemove(t)}>Delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * SendCatalog asks who fills each role on a library template, and how the
 * links reach them. It offered no delivery choice although the API takes one,
 * so a Library document was the one send whose links could only be handed
 * back to the sender.
 */
export function SendCatalog({
  session,
  detail,
  onCancel,
}: {
  session: AuthenticatedSession
  detail: CatalogDetail
  onCancel: () => void
}) {
  const navigate = useNavigate()
  const { entry } = detail
  const roles = entry.roles

  const [orgID, setOrgID] = useState(session.memberships[0]?.organization_id ?? '')
  const [title, setTitle] = useState(entry.name)
  const [people, setPeople] = useState<Record<string, { name: string; email: string }>>(
    Object.fromEntries(roles.map((r) => [r.id, { name: '', email: '' }])),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<{ id: string; recipients: CreatedRecipient[] } | null>(null)
  const [copied, setCopied] = useState('')
  const [deliverLinks, setDeliverLinks] = useState(false)
  const [remindersEnabled, setRemindersEnabled] = useState(true)
  // As on the sending page: the period picked, kept only while the chosen
  // organization may keep a document that long.
  const [chosenYears, setChosenYears] = useState<number | null>(null)
  const maxRetentionDays = session.billing?.find((b) => b.organization_id === orgID)?.max_retention_days

  const ordered = roles.some((r) => r.routing_order > 1)
  const ready = orgID && roles.every((r) => people[r.id]?.name.trim() && people[r.id]?.email.trim())

  function patch(roleId: string, change: Partial<{ name: string; email: string }>) {
    setPeople((p) => ({ ...p, [roleId]: { ...p[roleId], ...change } }))
  }

  async function send() {
    setBusy(true)
    setError('')
    try {
      const res = await sendCatalogEntry(entry.slug, {
        organization_id: orgID,
        title: title.trim() || undefined,
        retention_days: retentionDaysOf(keptFor(maxRetentionDays, chosenYears)),
        deliver_links: deliverLinks,
        reminders_enabled: deliverLinks ? remindersEnabled : undefined,
        assignments: roles.map((r) => ({
          role_id: r.id,
          name: people[r.id].name.trim(),
          email: people[r.id].email.trim(),
        })),
      })
      setSent({ id: res.envelope.id, recipients: res.recipients })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send that template.')
    } finally {
      setBusy(false)
    }
  }

  async function copy(url: string, key: string) {
    await navigator.clipboard.writeText(url)
    setCopied(key)
    setTimeout(() => setCopied(''), 1800)
  }

  const allFields = roles.flatMap((r) =>
    r.fields.map((f) => ({ ...f, role_id: r.id })),
  )

  if (sent) {
    return (
      <main className="page page-narrow">
        <div className="page-head">
          <h1>Sent</h1>
          <p>
            {ordered
              ? `Each person gets their own link, and they sign in the order the template sets.`
              : `Each person gets their own link and can sign in any order.`}
          </p>
        </div>
        <div className="stack">
          {sent.recipients.map((r) => (
            <div className="card" key={r.participation_id}>
              <div className="row-between" style={{ marginBottom: '.6rem' }}>
                <label htmlFor={`link-${r.participation_id}`} style={{ marginBottom: 0 }}>
                  {r.name} <span className="muted small">&lt;{r.email}&gt;</span>
                </label>
              </div>
              {r.signing_url ? (
                <div className="copy-field">
                  <input id={`link-${r.participation_id}`} type="text" readOnly value={r.signing_url}
                    onFocus={(e) => e.target.select()} />
                  <button
                    className="btn btn-secondary"
                    onClick={() => void copy(r.signing_url!, r.participation_id)}
                  >
                    {copied === r.participation_id ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : r.delivered === false ? (
                // Refused by the mail provider: saying "Sent to" here would
                // have the sender wait for a signature that cannot arrive.
                <p className="small" style={{ margin: 0, color: 'var(--bad)' }}>
                  Could not be delivered to {r.sent_to ?? r.email}. Reissue the link from the
                  envelope to try again.
                </p>
              ) : (
                <p className="small muted" style={{ margin: 0 }}>Sent to {r.sent_to ?? r.email}.</p>
              )}
            </div>
          ))}
        </div>
        <div className="row" style={{ marginTop: '1rem' }}>
          <button className="btn" onClick={() => navigate(`/envelopes/${sent.id}`)}>View envelope</button>
          <button className="btn btn-secondary" onClick={onCancel}>Back to templates</button>
        </div>
      </main>
    )
  }

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <h1>Send &ldquo;{entry.name}&rdquo;</h1>
        <p>
          {alreadySet(1, allFields.length)} Say who is filling each role.
        </p>
      </div>

      <div className="stack">
        <div className="card">
          <div style={{ marginBottom: '.9rem' }}>
            <label htmlFor="tpl-title">Title for this send</label>
            <input id="tpl-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <OrganizationPicker session={session} value={orgID} onChange={setOrgID} />
          <SentBy session={session} organizationID={orgID} />
          <DeliveryChoices
            deliverLinks={deliverLinks}
            onDeliverLinks={setDeliverLinks}
            remindersEnabled={remindersEnabled}
            onRemindersEnabled={setRemindersEnabled}
          />
          <KeepFor maxDays={maxRetentionDays} chosen={chosenYears} onChoose={setChosenYears} />
        </div>

        {roles.map((role) => {
          const mine = role.fields
          return (
            <div className="card" key={role.id}>
              <div className="row-between" style={{ marginBottom: '.6rem' }}>
                <strong>{role.name}</strong>
                {ordered && <span className="small faint">Signs {ordinal(role.routing_order)}</span>}
              </div>
              <div style={{ marginBottom: '.6rem' }}>
                <input type="text" placeholder="Name" value={people[role.id]?.name ?? ''}
                  onChange={(e) => patch(role.id, { name: e.target.value })} />
              </div>
              <input type="email" placeholder="name@example.com" value={people[role.id]?.email ?? ''}
                onChange={(e) => patch(role.id, { email: e.target.value })} />
              {/* A role saved with no boxes at all is one the server flags
                  (role_without_signature); it read "Fills ." under it. */}
              {mine.length > 0 && (
                <p className="small faint" style={{ marginTop: '.5rem', marginBottom: 0 }}>
                  Fills {mine.map((f) => f.label || kindLabel(f.kind).toLowerCase()).join(', ')}.
                </p>
              )}
            </div>
          )
        })}

        {error && <div className="notice notice-bad">{error}</div>}

        <button className="btn btn-lg" disabled={!ready || busy} onClick={send}>
          {busy ? <><span className="spinner" /> Sending…</> : 'Send for signature'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </main>
  )
}

/**
 * Whether a saved template's send can go: the server has not said the
 * template needs making again, and every role has a name and an address.
 *
 * The page ran its own check of a stored template's "Show only when" rules,
 * and the server now judges a stored template itself (needs_rebuilding), for
 * that rule and for a role with nothing to sign. Two checks of one template
 * can disagree, and the page's caught only one of the two faults, so the
 * server's answer is the only one read (R7-web).
 */
export function templateSendable(
  detail: Pick<TemplateDetail, 'roles' | 'needs_rebuilding'>,
  people: Record<string, { name: string; email: string } | undefined>,
): boolean {
  return !detail.needs_rebuilding
    && detail.roles.every((r) => !!people[r.id]?.name.trim() && !!people[r.id]?.email.trim())
}

/**
 * What the page says when a send of a saved template is refused: the server's
 * sentence as it comes. 400 template_needs_rebuilding carries the sentence the
 * template's read does, with the way to make it again, and bad_show_when names
 * the person and the box. The page once added its own next step to the second,
 * which spoke for a check the page no longer runs.
 */
export function templateSendRefusal(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Could not send that template.'
}

/** SendTemplate asks for the only thing a template does not already know. */
export function SendTemplate({
  session,
  detail,
  onCancel,
}: {
  session: AuthenticatedSession
  detail: TemplateDetail
  onCancel: () => void
}) {
  const navigate = useNavigate()
  const { template, roles, fields } = detail

  const [title, setTitle] = useState(template.name)
  const [people, setPeople] = useState<Record<string, { name: string; email: string }>>(
    Object.fromEntries(roles.map((r) => [r.id, { name: '', email: '' }])),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<{ id: string; recipients: CreatedRecipient[] } | null>(null)
  const [copied, setCopied] = useState('')

  const ordered = roles.some((r) => r.routing_order > 1)
  // A template saved before a rule that now refuses it cannot be sent. The
  // server says so when the template is read, and its sentence names the
  // role, the box and how to make the template again; it is shown before
  // the form is filled, and Send stays off.
  const problem = detail.needs_rebuilding
  const ready = templateSendable(detail, people)

  function patch(roleId: string, change: Partial<{ name: string; email: string }>) {
    setPeople((p) => ({ ...p, [roleId]: { ...p[roleId], ...change } }))
  }

  const [deliverLinks, setDeliverLinks] = useState(false)
  const [remindersEnabled, setRemindersEnabled] = useState(true)
  // The template's own period is the default, when its organization may keep
  // a document that long. The form had no choice at all, so a sender could
  // not keep one send of a template for less (WD27 of the apps' record).
  const [chosenYears, setChosenYears] = useState<number | null>(null)
  const maxRetentionDays = session.billing?.find((b) => b.organization_id === template.organization_id)
    ?.max_retention_days

  async function send() {
    setBusy(true)
    setError('')
    try {
      const res = await sendTemplate(template.id, {
        title: title.trim() || undefined,
        retention_days: retentionDaysOf(keptFor(maxRetentionDays, chosenYears, template.retention_days)),
        deliver_links: deliverLinks,
        reminders_enabled: deliverLinks ? remindersEnabled : undefined,
        assignments: roles.map((r) => ({
          role_id: r.id,
          name: people[r.id].name.trim(),
          email: people[r.id].email.trim(),
        })),
      })
      setSent({ id: res.envelope.id, recipients: res.recipients })
    } catch (e) {
      setError(templateSendRefusal(e))
    } finally {
      setBusy(false)
    }
  }

  async function copy(url: string, key: string) {
    await navigator.clipboard.writeText(url)
    setCopied(key)
    setTimeout(() => setCopied(''), 1800)
  }

  if (sent) {
    return (
      <main className="page page-narrow">
        <div className="page-head">
          <h1>Sent</h1>
          <p>
            {ordered
              ? `Each person gets their own link, and they sign in the order the template sets.`
              : `Each person gets their own link and can sign in any order.`}
          </p>
        </div>
        <div className="stack">
          {sent.recipients.map((r) => (
            <div className="card" key={r.participation_id}>
              <div className="row-between" style={{ marginBottom: '.6rem' }}>
                <label htmlFor={`link-${r.participation_id}`} style={{ marginBottom: 0 }}>
                  {r.name} <span className="muted small">&lt;{r.email}&gt;</span>
                </label>
              </div>
              {r.signing_url ? (
                <div className="copy-field">
                  <input id={`link-${r.participation_id}`} type="text" readOnly value={r.signing_url}
                    onFocus={(e) => e.target.select()} />
                  <button
                    className="btn btn-secondary"
                    onClick={() => void copy(r.signing_url!, r.participation_id)}
                  >
                    {copied === r.participation_id ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : (
                r.delivered === false ? (
                  <p className="small" style={{ margin: 0, color: 'var(--bad)' }}>
                    Could not be delivered to {r.sent_to ?? r.email}. Reissue the link from the
                    envelope to try again.
                  </p>
                ) : (
                  <p className="small muted" style={{ margin: 0 }}>Sent to {r.sent_to ?? r.email}.</p>
                )
              )}
            </div>
          ))}
        </div>
        <div className="row" style={{ marginTop: '1rem' }}>
          <button className="btn" onClick={() => navigate(`/envelopes/${sent.id}`)}>View envelope</button>
          <button className="btn btn-secondary" onClick={onCancel}>Back to templates</button>
        </div>
      </main>
    )
  }

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <h1>Send &ldquo;{template.name}&rdquo;</h1>
        <p>
          {alreadySet(detail.items.length || template.document_count || 1, fields.length)} Say who is
          filling each role.
        </p>
      </div>

      <div className="stack">
        <div className="card">
          <div style={{ marginBottom: '.9rem' }}>
            <label htmlFor="tpl-title">Title for this send</label>
            <input id="tpl-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <SentBy session={session} organizationID={template.organization_id} />
          <DeliveryChoices
            deliverLinks={deliverLinks}
            onDeliverLinks={setDeliverLinks}
            remindersEnabled={remindersEnabled}
            onRemindersEnabled={setRemindersEnabled}
          />
          <KeepFor
            maxDays={maxRetentionDays}
            chosen={chosenYears}
            onChoose={setChosenYears}
            templateDays={template.retention_days}
          />
        </div>

        {problem && (
          <div id="template-needs-rebuilding" className="notice notice-bad">{problem.message}</div>
        )}

        {roles.map((role) => {
          const mine = fields.filter((f) => f.role_id === role.id)
          return (
            <div className="card" key={role.id}>
              <div className="row-between" style={{ marginBottom: '.6rem' }}>
                <strong>{role.name}</strong>
                {ordered && <span className="small faint">Signs {ordinal(role.routing_order)}</span>}
              </div>
              <div style={{ marginBottom: '.6rem' }}>
                <input type="text" placeholder="Name" value={people[role.id]?.name ?? ''}
                  onChange={(e) => patch(role.id, { name: e.target.value })} />
              </div>
              <input type="email" placeholder="name@example.com" value={people[role.id]?.email ?? ''}
                onChange={(e) => patch(role.id, { email: e.target.value })} />
              {/* A role saved with no boxes at all is one the server flags
                  (role_without_signature); it read "Fills ." under it. */}
              {mine.length > 0 && (
                <p className="small faint" style={{ marginTop: '.5rem', marginBottom: 0 }}>
                  Fills {mine.map((f) => f.label || kindLabel(f.kind).toLowerCase()).join(', ')}.
                </p>
              )}
            </div>
          )
        })}

        {error && <div className="notice notice-bad">{error}</div>}

        <button className="btn btn-lg" disabled={!ready || busy} onClick={send}>
          {busy ? <><span className="spinner" /> Sending…</> : 'Send for signature'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </main>
  )
}

/**
 * Whether docsdone emails the links or hands them back, and whether it then
 * reminds, on both of this page's sends, as on the sending page. The first
 * changes what the certificate can claim about each address. The second is on
 * by default and offered only when docsdone emails the links: a link the
 * sender handed on is one docsdone has nothing to send again.
 */
export function DeliveryChoices({
  deliverLinks,
  onDeliverLinks,
  remindersEnabled,
  onRemindersEnabled,
}: {
  deliverLinks: boolean
  onDeliverLinks: (next: boolean) => void
  remindersEnabled: boolean
  onRemindersEnabled: (next: boolean) => void
}) {
  return (
    <>
      <div className="check-explained check-explained-block">
        <input
          id="deliver-links"
          type="checkbox"
          checked={deliverLinks}
          onChange={(e) => onDeliverLinks(e.target.checked)}
          aria-describedby="deliver-links-why"
        />
        <label htmlFor="deliver-links" style={{ marginBottom: 0 }}>
          Email each link from docsdone
        </label>
        <div id="deliver-links-why" className="small faint why">
          docsdone emails each person and records that the message was sent.
        </div>
      </div>
      {deliverLinks && (
        <div className="check-explained check-explained-block">
          <input
            id="reminders-enabled"
            type="checkbox"
            checked={remindersEnabled}
            onChange={(e) => onRemindersEnabled(e.target.checked)}
            aria-describedby="reminders-enabled-why"
          />
          <label htmlFor="reminders-enabled" style={{ marginBottom: 0 }}>
            Remind them automatically
          </label>
          <div id="reminders-enabled-why" className="small faint why">
            Sent 1, 3, and 7 days after the link, to anyone who has not signed and whose turn it is.
          </div>
        </div>
      )}
    </>
  )
}

function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'
  return `${n}${suffix}`
}
